import { Circle as CircleStyle, Fill, RegularShape, Stroke, Style, Text } from 'ol/style.js';
import { LineString, Point } from 'ol/geom.js';
import { getArea, getLength, getDistance } from 'ol/sphere.js';
import { toLonLat, transform, fromLonLat } from 'ol/proj';
import { config } from '../../config/Config';
import { toast } from 'react-toastify';
import GeoJSON from 'ol/format/GeoJSON';
import Polygon, { circular } from 'ol/geom/Polygon';
import GML3 from 'ol/format/GML3.js';
import { getTranslatedKey } from '../Utils/TranslateUtils';
import { Feature } from 'ol';

// Documento que define el estilo de las medidas realizadas
const styleFilter = new Style({
  fill: new Fill({
    color: 'rgba(254, 191, 0, 0.2)'
  }),
  stroke: new Stroke({
    color: 'rgba(254, 191, 0, 1)',
    lineDash: [10, 10],
    width: 2
  }),
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({
      color: 'rgba(254, 191, 0, 0.7)'
    }),
    fill: new Fill({
      color: 'rgba(254, 191, 0, 0.2)'
    })
  })
});

const styleMesura = new Style({
  fill: new Fill({
    color: 'rgba(192, 0, 0, 0.2)'
  }),
  stroke: new Stroke({
    color: 'rgba(192, 0, 0, 1)',
    // lineDash: [10, 10],
    width: 2
  }),
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({
      color: 'rgba(192, 0, 0, 1)'
    }),
    fill: new Fill({
      color: 'rgba(192, 0, 0, 0.2)'
    })
  })
});

const labelStyle = new Style({
  text: new Text({
    font: '14px Calibri,sans-serif',
    fill: new Fill({
      color: 'rgba(255, 255, 255, 1)'
    }),
    backgroundFill: new Fill({
      color: 'rgba(0, 0, 0, 0.7)'
    }),
    padding: [3, 3, 3, 3],
    textBaseline: 'bottom',
    offsetY: -15
  }),
  image: new RegularShape({
    radius: 8,
    points: 3,
    angle: Math.PI,
    displacement: [0, 8.5],
    fill: new Fill({
      color: 'rgba(0, 0, 0, 0.7)'
    })
  })
});

const segmentStyle = new Style({
  text: new Text({
    font: '12px Calibri,sans-serif',
    fill: new Fill({
      color: 'rgba(255, 255, 255, 1)'
    }),
    backgroundFill: new Fill({
      color: 'rgba(0, 0, 0, 0.4)'
    }),
    padding: [2, 2, 2, 2],
    textBaseline: 'bottom',
    offsetY: -12
  }),
  image: new RegularShape({
    radius: 6,
    points: 3,
    angle: Math.PI,
    displacement: [0, 7.5],
    fill: new Fill({
      color: 'rgba(0, 0, 0, 0.4)'
    })
  })
});

const segmentStyles = [segmentStyle];

const formatLength = (line) => {
  const length = getLength(line);
  let output;
  if (length > 1000) {
    output = Math.round((length / 1000) * 100) / 100 + ' km';
  } else {
    output = Math.round(length * 100) / 100 + ' m';
  }
  return output;
};

const formatArea = (polygon, isFilter) => {
  const area = getArea(polygon);
  let output;
  if (isFilter) {
    if (area > 5000) {
      output = Math.round((area / 10000) * 100) / 100 + ' ha';
    } else {
      output = Math.round(area * 100) / 100 + ' m\xB2';
    }
    return output;
  } else {
    if (area > 10000) {
      output = Math.round((area / 1000000) * 100) / 100 + ' km\xB2';
    } else {
      output = Math.round(area * 100) / 100 + ' m\xB2';
    }
    return output;
  }
};

const formatPerimeter = function (polygon) {
  let coordinates = polygon.getCoordinates()[0];
  coordinates = coordinates.map((coord) => {
    return toLonLat(coord, config.EPSG_mapa);
  });

  let perimeter = 0;

  for (var i = 0; i < coordinates.length - 1; i++) {
    perimeter += getDistance(coordinates[i], coordinates[i + 1]);
  }
  let output;
  if (perimeter > 1000) {
    output = Math.round((perimeter / 1000) * 100) / 100 + ' km';
  } else {
    output = Math.round(perimeter) + ' m';
  }
  return output;
};

const formatRadius = (geometry) => {
  let radius = geometry.getRadius();
  radius = radius * 0.74;

  let output;
  if (radius > 1000) {
    output = Math.round((radius / 1000) * 100) / 100 + ' km';
  } else {
    output = Math.round(radius) + ' m';
  }
  return 'Radi: ' + output;
};

export const styleFunction = (feature, segments, pointLabel, drawType, isFilter, showMedidas) => {
  const styles = isFilter ? [styleFilter] : [styleMesura];
  const geometry = feature.getGeometry();
  const type = geometry.getType();
  let point, label, line;
  if (!drawType || drawType === type) {
    if (type === 'Polygon') {
      // point = geometry.getInteriorPoint(); // Si quisieramos enseñar el área en el centro del polígono
      point = new Point(geometry.getLastCoordinate());
      label = !isFilter ? 'Àrea: ' + formatArea(geometry) + ' \nPerímetre: ' + formatPerimeter(geometry) : 'Àrea: ' + formatArea(geometry, isFilter);
      line = new LineString(geometry.getCoordinates()[0]);
    } else if (type === 'LineString') {
      point = new Point(geometry.getLastCoordinate());
      label = pointLabel ? formatLength(geometry) : null;
      line = geometry;
    } else if (type === 'Circle') {
      point = new Point(geometry.getCenter(geometry.flatCoordinates));
      label = formatRadius(geometry);
    } else if (type === 'Point') {
      if (feature.get('text')) {
        label = feature.get('text');
      } else {
        point = new Point(geometry.getLastCoordinate());
        let coords = geometry.getCoordinates();
        coords = transform(coords, config.EPSG_mapa, config.EPSG).map((coord) => coord.toFixed(2));
        label = coords.join(' ');
      }
    }
  }
  if (segments && line) {
    let count = 0;
    line.forEachSegment((a, b) => {
      const segment = new LineString([a, b]);
      const label = formatLength(segment);
      if (segmentStyles.length - 1 < count) {
        segmentStyles.push(segmentStyle.clone());
      }
      const segmentPoint = new Point(segment.getCoordinateAt(0.5));
      segmentStyles[count].setGeometry(segmentPoint);
      segmentStyles[count].getText().setText(label);
      styles.push(segmentStyles[count]);
      count++;
    });
  }

  if (feature.get('text') || (label && showMedidas)) {
    labelStyle.setGeometry(point);
    labelStyle.getText().setText(label);
    styles.push(labelStyle);
  }
  return styles;
};

export const descarregar = (format, source) => {
  const originalFeatures = source.getFeatures();
  const fc = [];
  originalFeatures.forEach((feat) => {
    let clonedFeature = feat.clone();
    if (clonedFeature.getGeometry().getType() === 'Circle') {
      let center = clonedFeature.getGeometry().getCenter();
      const radius = clonedFeature.getGeometry().getRadius();
      center = toLonLat(center, config.EPSG);
      let polygon = circular(center, radius);
      let circlePolConverted = polygon.getCoordinates()[0].map((coord) => {
        return fromLonLat(coord, config.EPSG);
      });
      let pol = new Polygon([circlePolConverted]);
      clonedFeature = new Feature({ geometry: pol });
      clonedFeature.setProperties({ isCircle: true });
    }
    clonedFeature.setProperties({ redlining: true });
    if (!feat.getProperties().text) {
      fc.push(clonedFeature);
    }
  });

  if (format === 'json') {
    const jsonFormat = new GeoJSON();
    const geoJSON = jsonFormat.writeFeatures(fc, {
      featureProjection: config.EPSG_mapa,
      dataProjection: config.EPSG
    });
    const geoJSONObject = JSON.parse(geoJSON);
    geoJSONObject.crs = {
      type: 'name',
      properties: {
        name: config.EPSG
      }
    };
    const updatedGeoJSON = JSON.stringify(geoJSONObject);
    const blob = new Blob([updatedGeoJSON], { type: 'application/json' });
    startDownlaod(blob, 'json');
  } else if (format === 'gml') {
    const gmlFormat = new GML3({
      featureType: 'Feature',
      featureNS: 'https://www.opengeospatial.org',
      srsName: config.EPSG
    });
    const gml = gmlFormat.writeFeatures(fc, {
      featureProjection: config.EPSG_mapa,
      dataProjection: config.EPSG
    });
    const blob = new Blob([gml], { type: 'application/gml' });
    startDownlaod(blob, 'gml');
  }
};

const startDownlaod = (blob, format) => {
  const downloadLink = document.createElement('a');
  downloadLink.href = URL.createObjectURL(blob);
  downloadLink.download = `dibuix.${format}`;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  toast.success(getTranslatedKey('successDownloadFile'));
};
